import React, { useEffect, useMemo, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid } from "@material-ui/core";
import InputText from "../../../../components/Inputs/InputText";
import InputAutoComplete from "../../../../components/Inputs/InputAutoComplete";
import { useNotification } from "../../../../Utils/hooks";
import { getTFGTitulations } from "../../../../services/TFG";

const TFGInstructorAllocationCreateDialog = ({ coord, academicYear, visible, onHide, onSave }) => {
  const [degrees, setDegrees] = useState([]);
  const [selectedDegrees, setSelectedDegrees] = useState([]); // array of { externalId, name }
  const [limit, setLimit] = useState(0);
  const notification = useNotification();

  useEffect(() => {
    async function retrieveTitulations(term) {
      try {
        const result = await getTFGTitulations(term, true).then((res) =>
          res.map((item) => ({ externalId: item.externalId, name: item.name }))
        );
        setDegrees(result || []);
        if (!result || !result.length) {
          notification("error", "No se han encontrado titulaciones con áreas temáticas disponibles");
        }
      } catch (e) {
        notification("error", "Ha habido un error al obtener las titulaciones");
      }
    }
    if (academicYear) retrieveTitulations(academicYear);
  }, [academicYear, notification]);

  // Compute degrees already configured in existing allocations for the selected academic year
  const filteredDegrees = useMemo(() => {
    if (!degrees?.length) return [];
    const termAllocations = (coord?.allocations || []).filter(
      (a) => a.academicYearId === academicYear
    );
    const excludedIds = new Set(
      termAllocations
        .map((a) => (a.degrees || []).map((d) => d?.externalId))
        .flat()
        .filter(Boolean)
    );
    return degrees.filter((d) => !excludedIds.has(d.externalId));
  }, [degrees, coord, academicYear]);

  // Ensure selected degrees remain valid after filtering
  useEffect(() => {
    if (!selectedDegrees?.length) return;
    const allowedIds = new Set(filteredDegrees.map((d) => d.externalId));
    const pruned = selectedDegrees.filter((d) => allowedIds.has(d.externalId));
    if (pruned.length !== selectedDegrees.length) setSelectedDegrees(pruned);
  }, [filteredDegrees, selectedDegrees]);

  const degreeValues = useMemo(() => selectedDegrees.map((d) => d.externalId), [selectedDegrees]);

  const onChangeDegrees = (selectedIds) => {
    // selectedIds is an array of externalIds (valueKey)
    const idSet = new Set(selectedIds || []);
    const opts = filteredDegrees.filter((d) => idSet.has(d.externalId));
    setSelectedDegrees(opts);
  };

  const onSubmit = async () => {
    if (!selectedDegrees?.length || !limit) {
      notification("error", "Por favor, rellene todos los campos");
      return;
    }
    const payload = {
      degrees: selectedDegrees.map((d) => ({ externalId: d.externalId, name: d.name })),
      limit: Number(limit),
      academicYearId: academicYear,
    };
    try {
      const result = await onSave(payload, true);
      // If save succeeds, close the dialog
      if (result !== false) {
        onHide();
      }
    } catch (e) {
      // Keep dialog open on error; notification should be handled upstream
    }
  };

  return (
    <Dialog open={visible} onClose={onHide} maxWidth={"sm"} fullWidth>
      <DialogTitle>{`Crear nueva asignación docente para ${coord?.instructorName} - ${academicYear}`}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <InputAutoComplete
              key="degree"
              name="degree"
              multiple={true}
              valueKey="externalId"
              labelKey="name"
              value={degreeValues}
              onChange={onChangeDegrees}
              label="Titulación"
              options={filteredDegrees}
              disabled={!filteredDegrees.length}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <InputText
              key="limit"
              name="limit"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              label="Límite"
              inputProps={{ type: "number", min: 1 }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="secondary" onClick={onHide}>
          Cancelar
        </Button>
        <Button variant="contained" color="primary" onClick={onSubmit}>
          Crear
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TFGInstructorAllocationCreateDialog